import { FormFields, FormHeader, FormSection } from '../form';
import { Checkbox, DatePicker } from '../../../../components';
import { useToggle, useValidation } from '../../../../hooks';
import { useFormContext } from 'react-hook-form';
import { claimFields } from '../../../../config';
import { useState } from 'react';

import type { Dispatch, FC, SetStateAction } from 'react';
import type { DateValue } from '@internationalized/date';
import { useContext } from 'react';
import { Localization } from '../../../../config/localization/localization';
import { LanguageSelectorContext } from '../../../../context/LanguageSelectorContext';

interface ITimesProps {
  setSaveAndQuitChanges?: Dispatch<SetStateAction<Array<string>>>;
}

export const Times: FC<ITimesProps> = ({ setSaveAndQuitChanges }) => {
  const formContext = useFormContext();
  const { fieldRequirements } = useValidation({});
  const { selectedLanguage } = useContext(LanguageSelectorContext);
  const localization = new Localization(selectedLanguage);

  const resetDamageDate = useToggle(false);
  const resetDiscoveryDate = useToggle(false);

  const damageDate = formContext.watch(claimFields.damageDate);
  const discoveryDate = formContext.watch(claimFields.discoveryDate);

  const isDamageDateUnknown: boolean = formContext.watch('isDamageDateUnknown') ?? false;
  const isDiscoveryDateSame: boolean = formContext.watch('isDiscoveryDateSame') ?? false;

  const [prevDamageDate, setPreviousDamageDate] = useState<DateValue>(damageDate);

  const onDamageDateChange = (newDate: DateValue) => {
    setSaveAndQuitChanges?.((prev) => [...prev, 'DamageDate']);
    formContext.setValue(claimFields.damageDate, newDate);
    formContext.setValue('isDamageDateUnknown', false);
    formContext.clearErrors(claimFields.damageDate);

    if (isDiscoveryDateSame) formContext.setValue(claimFields.discoveryDate, newDate);
  };

  const onDiscoveryDateChange = (newDate: DateValue) => {
    setSaveAndQuitChanges?.((prev) => [...prev, 'DamageDiscoveryDate']);
    formContext.setValue(claimFields.discoveryDate, newDate);
    formContext.setValue('isDiscoveryDateSame', false);
    formContext.clearErrors(claimFields.discoveryDate);
  };

  const onIsDamageDateUnknownChange = (isSelected: boolean) => {
    setSaveAndQuitChanges?.((prev) => [...prev, 'DamageDate']);
    formContext.setValue('isDamageDateUnknown', isSelected);
    formContext.clearErrors(claimFields.damageDate);

    if (isSelected) {
      formContext.setValue(claimFields.damageDate, null);
      setPreviousDamageDate(damageDate);
      formContext.clearErrors(claimFields.damageDate);
      formContext.clearErrors(claimFields.discoveryDate);
      formContext.setValue('isDiscoveryDateSame', false);
    } else {
      formContext.setValue(claimFields.damageDate, prevDamageDate);
    }
  };

  const onIsDiscoverySameChange = (isSelected: boolean) => {
    setSaveAndQuitChanges?.((prev) => [...prev, 'DamageDiscoveryDate']);
    formContext.setValue('isDiscoveryDateSame', isSelected);
    formContext.clearErrors('damageDiscoveryDate');

    if (isSelected) formContext.setValue(claimFields.discoveryDate, formContext.getValues(claimFields.damageDate));
  };

  const onResetDamageDate = () => {
    setSaveAndQuitChanges?.((prev) => [...prev, 'DamageDate']);
    formContext.setValue(claimFields.damageDate, null);
    if (isDiscoveryDateSame) formContext.setValue(claimFields.discoveryDate, null);
    resetDamageDate.toggle();
  };

  const onResetDiscoveryDate = () => {
    setSaveAndQuitChanges?.((prev) => [...prev, 'DamageDiscoveryDate']);
    formContext.setValue(claimFields.discoveryDate, null);
    resetDiscoveryDate.toggle();
  };

  return (
    <FormSection>
      <FormHeader>{localization.translate('times.header')}</FormHeader>

      <FormFields
        replaceClassName={
          'flex flex-col sm:flex-row justify-center mt-6 space-y-4 sm:space-y-0 sm:space-x-4 w-full'
        }
      >
        {/* DAMAGE DATE */}
        <div className={'flex w-full flex-col'}>
          {!isDamageDateUnknown && (
            <DatePicker
              customErrorMessage={localization.translate('times.damageDateError')}
              datePickerProps={{
                defaultValue: damageDate,
                granularity: 'minute',
                isDisabled: isDamageDateUnknown,
                isRequired: fieldRequirements.DamageDate,
                label: localization.translate('times.damageDateLabel'),
                maxValue: isDamageDateUnknown ? undefined : discoveryDate,
                onChange: onDamageDateChange,
                shouldCloseOnSelect: false,
              }}
              key={resetDamageDate.isOn ? 'reset' : 'normal'}
              name={claimFields.damageDate}
              onReset={onResetDamageDate}
              timeFieldLabel={localization.translate('times.damageTimeLabel')}
              tooltip={localization.translate('times.damageDateTooltip')}
            />
          )}
          <Checkbox
            options={{
              'aria-label': localization.translate('times.damageDateLabel'),
              isSelected: isDamageDateUnknown,
              onChange: onIsDamageDateUnknownChange,
            }}
          >
            <div className={'font-semibold'}>{localization.translate('times.damageDateUnknownCheckbox')}</div>
          </Checkbox>
        </div>

        {/* DISCOVERY DATE */}
        <div className={'flex w-full flex-col'}>
          {!isDiscoveryDateSame && (
            <DatePicker
              customErrorMessage={localization.translate('times.discoveryDateError')}
              datePickerProps={{
                defaultValue: discoveryDate,
                granularity: 'minute',
                isDisabled: isDiscoveryDateSame,
                isRequired: fieldRequirements.DiscoveryDate,
                label: localization.translate('times.discoveryDateLabel'),
                minValue: damageDate,
                onChange: onDiscoveryDateChange,
                shouldCloseOnSelect: false,
              }}
              key={resetDiscoveryDate.isOn ? 'reset' : 'normal'}
              name={claimFields.discoveryDate}
              onReset={onResetDiscoveryDate}
              timeFieldLabel={localization.translate('times.discoveryTimeLabel')}
              tooltip={localization.translate('times.discoveryDateTooltip')}
            />
          )}
          <Checkbox
            options={{
              'aria-label': localization.translate('times.discoveryDateLabel'),
              isDisabled: isDamageDateUnknown,
              isSelected: isDiscoveryDateSame,
              name: 'isDiscoveryDateSame',
              onChange: onIsDiscoverySameChange,
            }}
          >
            <div className={'font-semibold'}>
              {localization.translate('times.discoveryDateSameCheckbox')}
            </div>
          </Checkbox>
        </div>
      </FormFields>
    </FormSection>
  );
};